import { CSSProperties, FC, useEffect, useRef } from "react";

interface CreationCelebrationProps {
  isActive: boolean;
  onComplete: () => void;
}

interface FloatingLeaf {
  emoji: string;
  left: number;
  drift: number;
  spin: number;
  delay: number;
  size: number;
}

const CELEBRATION_DURATION_MS = 2600;

const FLOATING_LEAF_LIST: readonly FloatingLeaf[] = [
  { emoji: "🌱", left: 50, drift: -16, spin: -20, delay: 0, size: 2.2 },
  { emoji: "🍃", left: 42, drift: -40, spin: -38, delay: 0.14, size: 1.6 },
  { emoji: "🌿", left: 58, drift: 34, spin: 30, delay: 0.08, size: 1.9 },
  { emoji: "🍃", left: 63, drift: 52, spin: 42, delay: 0.24, size: 1.4 },
  { emoji: "🌱", left: 38, drift: -52, spin: -32, delay: 0.32, size: 1.5 },
  { emoji: "🌿", left: 52, drift: 12, spin: 14, delay: 0.2, size: 1.7 },
];

export const CreationCelebration: FC<CreationCelebrationProps> = ({ isActive, onComplete }) => {
  const onCompleteRef = useRef(onComplete);

  useEffect(() => {
    onCompleteRef.current = onComplete;
  }, [onComplete]);

  useEffect(() => {
    if (!isActive) {
      return;
    }

    const timeoutId = setTimeout(() => onCompleteRef.current(), CELEBRATION_DURATION_MS);
    return () => clearTimeout(timeoutId);
  }, [isActive]);

  if (!isActive) {
    return null;
  }

  return (
    <div
      className="pointer-events-none fixed inset-0 z-[60] flex items-center justify-center overflow-hidden"
      aria-hidden="true"
      data-testid="creation-celebration"
    >
      <div className="relative h-44 w-44">
        {FLOATING_LEAF_LIST.map((leaf, index) => (
          <span
            key={index}
            className="animate-leaf-float absolute bottom-0 select-none leading-none drop-shadow-sm"
            style={
              {
                left: `${leaf.left}%`,
                fontSize: `${leaf.size}rem`,
                animationDelay: `${leaf.delay}s`,
                "--leaf-drift": `${leaf.drift}px`,
                "--leaf-spin": `${leaf.spin}deg`,
              } as CSSProperties
            }
          >
            {leaf.emoji}
          </span>
        ))}
      </div>
    </div>
  );
};
